import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_COVER_BYTES,
  PACKAGE_LAYOUT_DEFS,
  PACKAGE_PRICING_DEFS,
  PACKAGE_PRICING_MAP,
  packagePrice,
  type PackageItemView,
  type PackageLayout,
  type PackagePricing,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Combo } from '../../components/Combo';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import { CardPickerButton, useCardChoice } from '../../components/CardPicker';
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from '../../components/ColumnPicker';
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from '../../components/ColumnFilter';
import {
  Badge,
  CatViewSwitch,
  EmptyState,
  ErrorState,
  FormTop,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  TableSkeleton,
  useCatView,
} from '../../components/ui';

interface ServiceOption {
  id: string;
  name: string;
  unit: string;
  unitPrice: string;
  isActive: boolean;
}

interface PackageItemRow {
  id: string;
  serviceId: string;
  quantity: string;
  unitPrice: string | null;
  description: string | null;
  service: ServiceOption;
}

interface PackageRow {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  category: string | null;
  pricingMode: PackagePricing;
  fixedPrice: string | null;
  discountPercent: string | null;
  layout: PackageLayout;
  durationDays: number | null;
  isActive: boolean;
  itemCount: number;
  items: PackageItemRow[];
  pricing: { listTotal: number; price: number; saving: number; savingPercent: number };
}

interface PackagePage {
  items: PackageRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/** كتالوج أعمدة الباقات. */
const PACKAGE_COLUMNS: readonly ColumnDef[] = [
  { key: 'name', label: 'الباقة', fixed: true },
  { key: 'code', label: 'الكود' },
  { key: 'category', label: 'التصنيف' },
  { key: 'items', label: 'الخدمات' },
  { key: 'pricing', label: 'التسعير' },
  { key: 'listTotal', label: 'مجموع الأسعار', group: 'الأرقام' },
  { key: 'price', label: 'سعر الباقة', group: 'الأرقام' },
  { key: 'saving', label: 'التوفير', group: 'الأرقام' },
  { key: 'isActive', label: 'الحالة' },
];

const PACKAGE_NUMERIC = new Set(['listTotal', 'price', 'saving']);

/** أعمدة زرّ «فلتر» — تقابل `FILTERABLE` في packages.service.ts. */
const PACKAGE_FILTERS: FilterDef[] = [
  { key: 'name', label: 'الباقة', kind: 'text', sortable: true },
  { key: 'code', label: 'الكود', kind: 'text' },
  { key: 'category', label: 'التصنيف', kind: 'text' },
  {
    key: 'pricing',
    label: 'التسعير',
    kind: 'enum',
    options: PACKAGE_PRICING_DEFS.map((p) => ({ value: p.key, label: p.label })),
  },
];

export function PackagesList() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const { state, patch, page } = useListState();
  const { filters, setFilters } = useColumnFilters(state, patch);
  const columns = useMemo<ColumnDef[]>(() => [...PACKAGE_COLUMNS], []);
  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice('goyone.columns.packages', columns);
  const colDrag = useColumnDrag(columnState, setColumnState);
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [editing, setEditing] = useState<PackageRow | 'new' | null>(null);
  const [view, setView] = useCatView('goyone.view.packages');

  const canEdit = atLeast('MANAGER');
  const query = { ...state, q: debounced || undefined, page };

  const list = useQuery({
    queryKey: ['packages', query],
    queryFn: () => api<PackagePage>(`/sales/packages${qs(query)}`),
  });

  const categories = useQuery({
    queryKey: ['packages', 'categories'],
    queryFn: () => api<string[]>('/sales/packages/categories'),
    staleTime: 5 * 60_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['packages'] });
    setEditing(null);
  };

  const cardChoice = useCardChoice("goyone.stats.packages", ['count', 'avgPrice', 'topPrice', 'avgSaving']);
  const statItems = list.data ? packageCards(list.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({ key, label, group }));

  return (
    <div className="page">
      <PageHead
        title="باقات الخدمات"
        subtitle={
          list.data
            ? `${plural(list.data.total, WORDS.pkg)} تُدرج بضغطة واحدة في عروض الأسعار`
            : undefined
        }
      />

      {list.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.packages"
          defaults={['count', 'avgPrice', 'topPrice', 'avgSaving']}
          external={cardChoice}
        />
      )}

      {/* ترتيب الشريط كقوائم إدارة العملاء: بحثٌ فمرشّحات، والاختيارات والإضافة في طرفه. */}
      <div className="toolbar sticky-bar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الكود"
        />

        <select
          className="select"
          style={{ width: 'auto' }}
          value={state.isActive ?? ''}
          onChange={(e) => patch({ isActive: e.target.value || undefined })}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          <option value="true">النشطة</option>
          <option value="false">الموقوفة</option>
        </select>

        <span className="row" style={{ marginInlineStart: 'auto', gap: 'var(--sp-2)' }}>
          <FilterButton defs={PACKAGE_FILTERS} filters={filters} onChange={setFilters} />
          <CatViewSwitch view={view} onView={setView} />
          {view === 'table' && (
            <ColumnPickerButton
              columns={columns}
              state={columnState}
              onChange={setColumnState}
              onReset={resetColumns}
              touched={columnsTouched}
            />
          )}
          <CardPickerButton
            choices={statChoices}
            chosen={cardChoice.chosen}
            onChange={cardChoice.setChosen}
            onReset={cardChoice.reset}
          />
          {canEdit && (
            <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
              <Icon name="plus" size={14} />
              باقة جديدة
            </button>
          )}
        </span>
      </div>

      <FilterChips defs={PACKAGE_FILTERS} filters={filters} onChange={setFilters} />

      {(categories.data?.length ?? 0) > 0 && (
        <div className="cat-tabs" role="tablist" aria-label="التصنيفات">
          <button
            type="button"
            role="tab"
            aria-selected={!state.category}
            className={`cat-tab${state.category ? '' : ' is-on'}`}
            onClick={() => patch({ category: undefined })}
          >
            كل التصنيفات
          </button>
          {(categories.data ?? []).map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={state.category === c}
              className={`cat-tab${state.category === c ? ' is-on' : ''}`}
              onClick={() => patch({ category: c })}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {view === 'cards' ? (
        <>
          {list.isPending && <div className="skeleton" style={{ height: 220 }} />}

          {list.data && list.data.items.length > 0 && (
            <div className="cat-grid mb-4">
              {list.data.items.map((p) => (
                <PackageCard
                  key={p.id}
                  pkg={p}
                  canEdit={canEdit}
                  onEdit={() => setEditing(p)}
                />
              ))}
            </div>
          )}

          {list.isError && (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          )}

          {list.data?.items.length === 0 && (
            <div className="card">
              <EmptyState
                title="لا باقات هنا"
                text="جرّب تصنيفًا آخر، أو ابنِ باقةً من خدمات الكتالوج."
                action={
                  canEdit && (
                    <button
                      className="btn btn-primary"
                      onClick={() => setEditing('new')}
                    >
                      باقة جديدة
                    </button>
                  )
                }
              />
            </div>
          )}

          {list.data && list.data.pageCount > 1 && (
            <div className="card">
              <Pager
                page={list.data.page}
                pageCount={list.data.pageCount}
                total={list.data.total}
                pageSize={list.data.pageSize}
                onPage={(p) => patch({ page: String(p) })}
              />
            </div>
          )}
        </>
      ) : (
      <FilterScope
        defs={PACKAGE_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/sales/packages/distinct"
        query={query}
        sort={state.sort}
        order={state.order}
        onSort={(sort, order) => patch({ sort, order })}
      >
      <div className="card">
        <div className="table-wrap sticky-head">
          <table className="table">
            <thead>
              <tr>
                {shownColumns.map((c) => (
                  <th key={c.key} {...colDrag.thProps(c, PACKAGE_NUMERIC.has(c.key) ? 'col-num' : undefined)}>
                    {c.label}
                    <HeaderFilter colKey={c.key} />
                  </th>
                ))}
                {canEdit && <th className="col-pin-end" />}
              </tr>
            </thead>

            {list.isPending && <TableSkeleton cols={shownColumns.length + (canEdit ? 1 : 0)} />}

            {list.data && (
              <tbody>
                {list.data.items.map((p) => (
                  <tr key={p.id}>
                    {shownColumns.map((c) => (
                      <PackageCell key={c.key} column={c} pkg={p} />
                    ))}
                    {canEdit && (
                      <td className="end col-pin-end">
                        <button
                          className="btn btn-sm btn-icon btn-ghost"
                          onClick={() => setEditing(p)}
                          aria-label={`تعديل ${p.name}`}
                        >
                          <Icon name="edit" size={15} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {list.isError && <ErrorState error={list.error} onRetry={() => void list.refetch()} />}

        {list.data?.items.length === 0 && (
          <EmptyState
            title="لا توجد باقات"
            text="الباقة تجمع عدة خدمات بسعر واحد، فتُدرج في عرض السعر دفعة واحدة بدل اختيارها خدمة خدمة."
            action={
              canEdit && (
                <button className="btn btn-primary" onClick={() => setEditing('new')}>
                  باقة جديدة
                </button>
              )
            }
          />
        )}

        {list.data && (
          <Pager
            page={list.data.page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />
        )}
      </div>
      </FilterScope>
      )}

      {editing && (
        <PackageModal
          pkg={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

interface DraftItem {
  key: string;
  serviceId: string;
  quantity: string;
  unitPrice: string;
  description: string;
}

let draftKey = 0;
const newDraft = (): DraftItem => ({
  key: `d${draftKey++}`,
  serviceId: '',
  quantity: '1',
  unitPrice: '',
  description: '',
});

function PackageModal({
  pkg,
  onClose,
  onSaved,
}: {
  pkg: PackageRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  /* تصنيفاتُ الباقات كما سُمّيت من قبل — تُقترح ولا تُفرض. */
  const categories = useQuery({
    queryKey: ['packages', 'categories'],
    queryFn: () => api<string[]>('/sales/packages/categories'),
  });

  const services = useQuery({
    queryKey: ['services', 'for-package'],
    queryFn: () =>
      api<{ items: ServiceOption[] }>(
        `/sales/services${qs({ isActive: true, pageSize: 100 })}`,
      ),
  });

  const [form, setForm] = useState({
    code: pkg?.code ?? '',
    name: pkg?.name ?? '',
    description: pkg?.description ?? '',
    category: pkg?.category ?? '',
    imageUrl: pkg?.imageUrl ?? '',
    pricingMode: pkg?.pricingMode ?? ('SUM' as PackagePricing),
    fixedPrice: pkg?.fixedPrice ? String(Number(pkg.fixedPrice)) : '',
    discountPercent: pkg?.discountPercent ? String(Number(pkg.discountPercent)) : '',
    layout: pkg?.layout ?? ('DETAILED' as PackageLayout),
    durationDays: pkg?.durationDays ? String(pkg.durationDays) : '',
    isActive: pkg?.isActive ?? true,
  });

  const [items, setItems] = useState<DraftItem[]>(() =>
    pkg && pkg.items.length
      ? pkg.items.map((i) => ({
          key: `e${i.id}`,
          serviceId: i.serviceId,
          quantity: String(Number(i.quantity)),
          unitPrice: i.unitPrice ? String(Number(i.unitPrice)) : '',
          description: i.description ?? '',
        }))
      : [newDraft()],
  );

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const byId = useMemo(
    () => new Map((services.data?.items ?? []).map((s) => [s.id, s])),
    [services.data],
  );

  /*
   * المعاينة تُحسب بالدالة المشتركة نفسها التي يستخدمها الخادم، فما يظهر
   * هنا هو ما سيُحفظ. حساب مستقل في الواجهة كان سيتباعد عن الخادم يومًا ما.
   */
  const views: PackageItemView[] = items
    .filter((i) => i.serviceId)
    .map((i) => {
      const svc = byId.get(i.serviceId);
      return {
        serviceId: i.serviceId,
        description: i.description || svc?.name || '',
        unit: svc?.unit ?? 'خدمة',
        quantity: Number(i.quantity) || 0,
        unitPrice: i.unitPrice ? Number(i.unitPrice) : Number(svc?.unitPrice ?? 0),
      };
    });

  const preview = packagePrice(
    {
      pricingMode: form.pricingMode,
      fixedPrice: form.fixedPrice ? Number(form.fixedPrice) : null,
      discountPercent: form.discountPercent ? Number(form.discountPercent) : null,
    },
    views,
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code || undefined,
        name: form.name,
        description: form.description || undefined,
        category: form.category || undefined,
        imageUrl: form.imageUrl || null,
        pricingMode: form.pricingMode,
        fixedPrice: form.pricingMode === 'FIXED' ? Number(form.fixedPrice) || 0 : null,
        discountPercent:
          form.pricingMode === 'DISCOUNT' ? Number(form.discountPercent) || 0 : null,
        layout: form.layout,
        durationDays: form.durationDays ? Number(form.durationDays) : null,
        items: items
          .filter((i) => i.serviceId)
          .map((i) => ({
            serviceId: i.serviceId,
            quantity: Number(i.quantity) || 1,
            unitPrice: i.unitPrice ? Number(i.unitPrice) : null,
            description: i.description || undefined,
          })),
        isActive: form.isActive,
      };
      return pkg
        ? api(`/sales/packages/${pkg.id}`, { method: 'PATCH', body })
        : api('/sales/packages', { method: 'POST', body });
    },
    onSuccess: onSaved,
  });

  const remove = useMutation({
    mutationFn: () => api(`/sales/packages/${pkg!.id}`, { method: 'DELETE' }),
    onSuccess: onSaved,
  });

  const chosen = new Set(items.map((i) => i.serviceId).filter(Boolean));
  const pricingHint = PACKAGE_PRICING_DEFS.find((d) => d.key === form.pricingMode)?.hint;
  const layoutHint = PACKAGE_LAYOUT_DEFS.find((d) => d.key === form.layout)?.hint;

  const valid =
    form.name.trim().length >= 2 &&
    views.length > 0 &&
    (form.pricingMode !== 'FIXED' || form.fixedPrice !== '') &&
    (form.pricingMode !== 'DISCOUNT' || form.discountPercent !== '');

  return (
    <Modal
      title={pkg ? `تعديل ${pkg.name}` : 'باقة جديدة'}
      onClose={onClose}
      wide
      footer={
        <>
          {pkg && (
            <button
              className="btn btn-danger me-auto"
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              type="button"
            >
              {remove.isPending ? 'جارٍ الحذف' : 'حذف الباقة'}
            </button>
          )}
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {/*
        نموذجُ الباقة كنموذج الخدمة: غلافٌ وحالٌ في الرأس، ثمّ أبوابٌ لكلٍّ
        أيقونتُه. شاشتان تصنعان الشيءَ نفسَه بشكلين تجعلان القارئَ يتعلّم
        مرّتين، ويُصلَح عيبٌ في إحداهما ويبقى في الأخرى.
      */}
      <FormTop
        image={form.imageUrl}
        onImage={(v) => set('imageUrl', v)}
        active={form.isActive}
        onActive={(v) => set('isActive', v)}
        maxBytes={MAX_COVER_BYTES}
      />

      <section className="fx-sec">
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="clipboard" size={15} />
          </span>
          <span>
            <b className="fx-title">تعريف الباقة</b>
            <span className="fx-note">ما تُعرف به في الكتالوج والمستندات</span>
          </span>
        </header>

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="p-name">
            اسم الباقة
          </label>
          <input
            id="p-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            autoFocus
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="p-code">
            الكود
          </label>
          <input
            id="p-code"
            className="input"
            value={form.code}
            onChange={(e) => set('code', e.target.value)}
            dir="ltr"
            placeholder={pkg ? '' : 'يُولَّد تلقائيًّا'}
          />
          {!pkg && (
            <span className="tiny mute">
              اتركه فارغًا ليأخذ الرقم التالي — أو اكتب كودك.
            </span>
          )}
        </div>

        <div className="field">
          <label className="label" htmlFor="p-category">
            التصنيف
          </label>
          <Combo
            id="p-category"
            value={form.category}
            onChange={(v) => set('category', v)}
            options={categories.data ?? []}
            placeholder="اكتب أو اختر"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="p-desc">
            الوصف
          </label>
          <textarea
            id="p-desc"
            className="input"
            rows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <span className="tiny mute">يظهر للعميل عند عرض الباقة في المستند المجمّع.</span>
        </div>
      </div>
      </section>

      <section className="fx-sec" style={{ ['--fx-tone' as string]: 'var(--mv-2)' }}>
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="inbox" size={15} />
          </span>
          <span>
            <b className="fx-title">الخدمات داخل الباقة</b>
            <span className="fx-note">
              تُنسخ بنودُها إلى المستند عند الإدراج، ثمّ تستقلّ عنها
            </span>
          </span>
          <span className="row">
            <button
              className="btn btn-sm"
              type="button"
              onClick={() => setItems((list) => [...list, newDraft()])}
            >
              <Icon name="plus" size={13} />
              إضافة خدمة
            </button>
          </span>
        </header>

      <div className="table-wrap">
        <table className="table lines-table">
          <thead>
            <tr>
              <th style={{ width: '38%' }}>الخدمة</th>
              <th style={{ width: '12%' }}>الكمية</th>
              <th style={{ width: '18%' }}>سعر خاص</th>
              <th>وصف بديل</th>
              <th style={{ width: '12%' }}>القيمة</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const svc = byId.get(item.serviceId);
              const unitPrice = item.unitPrice
                ? Number(item.unitPrice)
                : Number(svc?.unitPrice ?? 0);
              const value = (Number(item.quantity) || 0) * unitPrice;

              return (
                <tr key={item.key}>
                  <td>
                    <select
                      className="input"
                      value={item.serviceId}
                      aria-label={`الخدمة ${index + 1}`}
                      onChange={(e) =>
                        setItems((list) =>
                          list.map((r) =>
                            r.key === item.key ? { ...r, serviceId: e.target.value } : r,
                          ),
                        )
                      }
                    >
                      <option value="">اختر خدمة</option>
                      {(services.data?.items ?? []).map((s) => (
                        <option
                          key={s.id}
                          value={s.id}
                          disabled={s.id !== item.serviceId && chosen.has(s.id)}
                        >
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min="0"
                      step="any"
                      aria-label={`كمية البند ${index + 1}`}
                      value={item.quantity}
                      onChange={(e) =>
                        setItems((list) =>
                          list.map((r) =>
                            r.key === item.key ? { ...r, quantity: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input num"
                      type="number"
                      min="0"
                      step="any"
                      aria-label={`سعر خاص للبند ${index + 1}`}
                      placeholder={svc ? String(Number(svc.unitPrice)) : ''}
                      value={item.unitPrice}
                      onChange={(e) =>
                        setItems((list) =>
                          list.map((r) =>
                            r.key === item.key ? { ...r, unitPrice: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="input"
                      aria-label={`وصف البند ${index + 1}`}
                      placeholder={svc?.name ?? ''}
                      value={item.description}
                      onChange={(e) =>
                        setItems((list) =>
                          list.map((r) =>
                            r.key === item.key ? { ...r, description: e.target.value } : r,
                          ),
                        )
                      }
                    />
                  </td>
                  <td className="num cell-mute">{money(value)}</td>
                  <td className="end">
                    <button
                      className="btn btn-sm btn-icon btn-ghost"
                      type="button"
                      aria-label={`حذف البند ${index + 1}`}
                      disabled={items.length === 1}
                      onClick={() => setItems((list) => list.filter((r) => r.key !== item.key))}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      </section>

      <section className="fx-sec" style={{ ['--fx-tone' as string]: 'var(--success)' }}>
        <header className="fx-head">
          <span className="fx-ico">
            <Icon name="wallet" size={15} />
          </span>
          <span>
            <b className="fx-title">التسعير</b>
            <span className="fx-note">{pricingHint}</span>
          </span>
          <span className="row">
            <span className="segr" role="radiogroup" aria-label="طريقة التسعير">
              {PACKAGE_PRICING_DEFS.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  role="radio"
                  aria-checked={form.pricingMode === d.key}
                  className={`segr-btn${form.pricingMode === d.key ? ' is-on' : ''}`}
                  onClick={() => set('pricingMode', d.key)}
                >
                  {d.label}
                </button>
              ))}
            </span>
          </span>
        </header>

      <div className="form-grid">
        {form.pricingMode === 'DISCOUNT' && (
          <div className="field">
            <label className="label req" htmlFor="p-discount">
              نسبة الخصم
            </label>
            <span className="in-suffix">
              <input
                id="p-discount"
                className="input num"
                type="number"
                min="0"
                max="100"
                step="any"
                value={form.discountPercent}
                onChange={(e) => set('discountPercent', e.target.value)}
              />
              <i>٪</i>
            </span>
          </div>
        )}

        {form.pricingMode === 'FIXED' && (
          <div className="field">
            <label className="label req" htmlFor="p-fixed">
              السعر المقطوع
            </label>
            <span className="in-suffix">
              <input
                id="p-fixed"
                className="input num"
                type="number"
                min="0"
                step="any"
                value={form.fixedPrice}
                onChange={(e) => set('fixedPrice', e.target.value)}
              />
              <i>ج.م</i>
            </span>
          </div>
        )}

        <div className="field">
          <label className="label" htmlFor="p-layout">
            شكل العرض في المستند
          </label>
          <select
            id="p-layout"
            className="input"
            value={form.layout}
            onChange={(e) => set('layout', e.target.value)}
          >
            {PACKAGE_LAYOUT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{layoutHint}</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="p-duration">
            مدة التنفيذ بالأيام
          </label>
          <input
            id="p-duration"
            className="input num"
            type="number"
            min="1"
            value={form.durationDays}
            onChange={(e) => set('durationDays', e.target.value)}
          />
        </div>
      </div>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">مجموع الأسعار المفردة</span>
          <span className="num">{money(preview.listTotal)}</span>
        </div>
        <div className="totals-row">
          <span className="mute">يوفّر العميل</span>
          <span className={`num ${preview.saving > 0 ? 'pos' : preview.saving < 0 ? 'neg' : ''}`}>
            {preview.saving === 0 ? '—' : `${money(preview.saving)} (${preview.savingPercent}٪)`}
          </span>
        </div>
        <div className="totals-row totals-final">
          <span>سعر الباقة</span>
          <span className="num">{money(preview.price)}</span>
        </div>
      </div>
      </section>
    </Modal>
  );
}

/**
 * بطاقةُ باقة — ما فيها وبكم، وكم يوفّر العميل.
 *
 * الباقةُ تُباع بفكرتها لا بجدولها: «ستُّ خدماتٍ في ثلاثين يومًا بتوفير
 * ٪١٥» جملةٌ تُقنع، وسبعةُ أعمدةٍ لا تُقنع. فالبطاقةُ تقولها، والجدولُ
 * يبقى لمن يقارن.
 */
function PackageCard({
  pkg,
  canEdit,
  onEdit,
}: {
  pkg: PackageRow;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const saved = pkg.pricing.saving > 0;

  return (
    <article className={`cat-card${pkg.isActive ? '' : ' is-off'}`}>
      <div className="cat-cover">
        {pkg.imageUrl ? (
          <img src={pkg.imageUrl} alt="" loading="lazy" />
        ) : (
          <span className="cat-cover-none">{pkg.name.slice(0, 1)}</span>
        )}
      </div>

      {saved && (
        <span className="cat-off">توفير {pkg.pricing.savingPercent}٪</span>
      )}

      <div className="cat-top">
        {pkg.category ? (
          <span className="cat-chip">{pkg.category}</span>
        ) : (
          <span className="cat-code">بلا تصنيف</span>
        )}
        {pkg.code && (
          <span className="cat-code num" dir="ltr">
            {pkg.code}
          </span>
        )}
        {canEdit && (
          <span className="cat-act">
            <button
              className="btn btn-sm btn-icon btn-ghost"
              onClick={onEdit}
              aria-label={`تعديل ${pkg.name}`}
            >
              <Icon name="edit" size={14} />
            </button>
          </span>
        )}
      </div>

      <h3 className="cat-name">{pkg.name}</h3>
      {pkg.description && <p className="cat-desc">{pkg.description}</p>}

      <div className="cat-meta">
        <span>
          <Icon name="inbox" size={12} />
          {plural(pkg.itemCount, WORDS.service)}
        </span>
        {pkg.durationDays != null && (
          <span>
            <Icon name="clock" size={12} />
            {plural(pkg.durationDays, WORDS.day)}
          </span>
        )}
        <span>
          <Icon name="layers" size={12} />
          {PACKAGE_PRICING_MAP[pkg.pricingMode].label}
        </span>
      </div>

      <div className="cat-foot">
        <span className="cat-price">
          <b className="num">{money(pkg.pricing.price)}</b>
          {saved && (
            <small className="num cat-was">{money(pkg.pricing.listTotal)}</small>
          )}
        </span>
        <span className="cat-tail">
          <Badge kind={pkg.isActive ? 'success' : 'neutral'}>
            {pkg.isActive ? 'متاحة' : 'موقوفة'}
          </Badge>
        </span>
      </div>
    </article>
  );
}

/** خليّةٌ واحدة، يُختار شكلها بمفتاح العمود لا بموضعه. */
function PackageCell({ column, pkg }: { column: ColumnDef; pkg: PackageRow }) {
  switch (column.key) {
    case 'name':
      return <td className="cell-strong">{pkg.name}</td>;
    case 'code':
      return <td className="num cell-mute">{pkg.code ?? '—'}</td>;
    case 'category':
      return <td className="cell-mute">{pkg.category ?? '—'}</td>;
    case 'items':
      return <td className="num cell-mute">{pkg.itemCount}</td>;
    case 'pricing':
      return (
        <td>
          <Badge kind={PACKAGE_PRICING_MAP[pkg.pricingMode].kind}>
            {PACKAGE_PRICING_MAP[pkg.pricingMode].label}
          </Badge>
        </td>
      );
    case 'listTotal':
      return <td className="num cell-mute">{money(pkg.pricing.listTotal)}</td>;
    case 'price':
      return <td className="num cell-strong">{money(pkg.pricing.price)}</td>;
    case 'saving':
      /* الموجب توفيرٌ للعميل، والسالب بيعٌ فوق مجموع الأسعار — واللون يفرّق. */
      return (
        <td className="num">
          {pkg.pricing.saving > 0 ? (
            <span className="pos">
              {money(pkg.pricing.saving)} ({pkg.pricing.savingPercent}٪)
            </span>
          ) : pkg.pricing.saving < 0 ? (
            <span className="neg">{money(pkg.pricing.saving)}</span>
          ) : (
            '—'
          )}
        </td>
      );
    case 'isActive':
      return (
        <td>
          <Badge kind={pkg.isActive ? 'success' : 'neutral'}>
            {pkg.isActive ? 'متاحة' : 'موقوفة'}
          </Badge>
        </td>
      );
    default:
      return <td>—</td>;
  }
}

/** بطاقات الباقات — الإجمالي من العدّاد، والباقي من هذه الصفحة بتصريح. */
function packageCards(page: PackagePage): RecordStat[] {
  const rows = page.items;
  const prices = rows.map((r) => r.pricing.price).filter((v) => Number.isFinite(v));
  const avg = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const top = prices.length ? Math.max(...prices) : 0;
  const savings = rows.map((r) => r.pricing.savingPercent).filter((v) => v > 0);
  const avgSaving = savings.length ? savings.reduce((a, b) => a + b, 0) / savings.length : 0;
  const inactive = rows.filter((r) => !r.isActive).length;
  const avgItems = rows.length
    ? rows.reduce((a, r) => a + r.itemCount, 0) / rows.length
    : 0;
  return [
    { key: 'count', label: 'الباقات', group: 'الكتالوج', icon: 'layers', color: '#8B5CF6', value: page.total, numeric: true },
    { key: 'avgPrice', label: 'متوسط سعر الباقة', group: 'الأسعار', icon: 'receipt', color: '#51A521', value: money(avg), numeric: true, note: 'من هذه الصفحة' },
    { key: 'topPrice', label: 'أعلى باقة', group: 'الأسعار', icon: 'chart', color: '#1D46A6', value: money(top), numeric: true, note: 'من هذه الصفحة' },
    { key: 'avgSaving', label: 'متوسط وفر الباقة', group: 'الأسعار', icon: 'zap', color: '#BF930B', value: avgSaving ? Math.round(avgSaving) + '٪' : '—', numeric: true, note: 'على الباقات المخفَّضة' },
    { key: 'avgItems', label: 'متوسط خدمات الباقة', group: 'الكتالوج', icon: 'inbox', color: '#5596FF', value: Math.round(avgItems * 10) / 10, numeric: true, note: 'من هذه الصفحة' },
    { key: 'inactive', label: 'موقوفة', group: 'الكتالوج', icon: 'alert', color: '#DC2626', value: inactive, numeric: true, note: 'من هذه الصفحة' },
  ];
}
